/**
 * Horizontal status banner for the submission status screen.
 *
 * Shows a status icon (spinner while in progress), the status title, and a
 * supporting subtitle. Replaces the old centered status block in the new design.
 *
 * @module statusBanner
 */
import { SpinningLoader } from "@risalabs_frontend_org/oasis-ui-kit";
import { SubmissionStatusEnum } from "../../../../data-model/submissionDocs";
import AlertCircleIcon from "../../../../svg/alert-circle-icon";
import { CheckCircleIcon } from "../../../../svg/check-circle-icon";
import CrossCircleIcon from "../../../../svg/cross-circle-icon";
import DownloadIcon from "../../../../svg/download-icon";
import EyeIcon from "../../../../svg/eye";
import { InfoIcon } from "../../../../svg/info-icon";
import SpinnerIcon from "../../../../svg/spinner-icon";
import UserCheckIcon from "../../../../svg/user-check-icon";
import WarningIcon from "../../../../svg/warningIcon";
import { getSubmissionStatusCopy } from "../../utils/submissionStatusCopy";

/** Props for {@link StatusBanner}. */
interface StatusBannerProps {
  status?: SubmissionStatusEnum | "";
  /** `workflow_config.submission_type`; fax flows get fax-specific copy. */
  submissionType?: string;
  showDocumentActions?: boolean;
  onDownloadDocument?: () => void;
  /** Shows a spinner and blocks repeat clicks while the file is being fetched. */
  isDownloadingDocument?: boolean;
  onViewScreenshot?: () => void;
  /** Fax id shown in a bar below the copy for delivered-fax submissions. */
  faxId?: string;
}

/**
 * "Download Document" / "View Screenshot" action links shown on the right of
 * the status banner for completed/failed/fax states. Screenshot is omitted when
 * no handler is provided (e.g. fax submissions have no portal screenshot).
 *
 * @input props - Click handlers for the document and screenshot actions, plus
 * the in-flight state of the document download.
 * @returns The action links row.
 */
const BannerDocumentActions = (props: {
  onDownloadDocument?: () => void;
  isDownloadingDocument?: boolean;
  onViewScreenshot?: () => void;
}) => (
  <div className="submission-status-banner__actions flex items-center gap-6">
    <button
      type="button"
      onClick={props.onDownloadDocument}
      disabled={props.isDownloadingDocument}
      aria-busy={props.isDownloadingDocument}
      className="flex items-center gap-1.5 text-tiny font-semibold leading-tiny text-tertiaryBlue-4 hover:underline disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
    >
      {props.isDownloadingDocument ? (
        <SpinnerIcon width="16" height="16" fill="#0056D6" />
      ) : (
        <DownloadIcon width={16} height={16} color="#0056D6" />
      )}
      {props.isDownloadingDocument
        ? "Downloading..."
        : "Download Clinical Attachment"}
    </button>
    {props.onViewScreenshot && (
      <button
        type="button"
        onClick={props.onViewScreenshot}
        className="flex items-center gap-1.5 text-tiny font-semibold leading-tiny text-tertiaryBlue-4 hover:underline"
      >
        <EyeIcon width={16} height={16} color="#0056D6" />
        View Screenshot
      </button>
    )}
  </div>
);

/**
 * Renders the submission status as a horizontal banner with icon and copy.
 *
 * @input props - The current submission status.
 * @returns The status banner element.
 */
const StatusBanner = (props: StatusBannerProps) => {
  const copy = getSubmissionStatusCopy(props.status, props.submissionType);

  const renderIcon = () => {
    if (!props.status) {
      return <InfoIcon height="24" width="24" stroke="#0056D6" />;
    }

    switch (props.status) {
      case "completed":
        return <CheckCircleIcon height="24" width="24" stroke="#008F32" />;
      case "submission_completed":
        return <UserCheckIcon height="24" width="24" stroke="#008F32" />;
      case "already_filed":
        return <CheckCircleIcon height="24" width="24" stroke="#008F32" />;
      case "fax_delivered":
        return <CheckCircleIcon height="24" width="24" stroke="#0056D6" />;
      case "error":
        return <AlertCircleIcon height="24" width="24" stroke="#F50400" />;
      case "saved_draft":
        return <AlertCircleIcon height="24" width="24" stroke="#EF6C00" />;
      case "fax_error":
      case "fax_failed":
        return <CrossCircleIcon height="24" width="24" stroke="#EF6C00" />;
      case "payer_not_supported":
        return <WarningIcon height="28" width="28" />;
      case "in_progress":
      case "fax_submitted":
      case "queued":
      default:
        return <SpinningLoader />;
    }
  };

  return (
    <div className="submission-status-banner flex flex-col items-start gap-4 self-stretch rounded-md border border-primaryGray-15 bg-white p-4">
      <div className="submission-status-banner__row flex w-full items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="submission-status-banner__icon flex h-7 w-7 items-center justify-center">
            {renderIcon()}
          </div>
          <div className="submission-status-banner__text flex flex-col gap-0.5">
            <div className="text-h11 font-bold leading-6 text-primaryGray-2">
              {copy.title}
            </div>
            <div className="self-stretch text-tiny font-normal leading-tiny tracking-tightest text-primaryGray-7">
              {copy.subtitle}
            </div>
          </div>
        </div>

        {props.showDocumentActions && (
          <BannerDocumentActions
            onDownloadDocument={props.onDownloadDocument}
            isDownloadingDocument={props.isDownloadingDocument}
            onViewScreenshot={props.onViewScreenshot}
          />
        )}
      </div>

      {props.faxId && (
        <div className="submission-status-banner__fax-id w-full rounded-md bg-primaryGray-16 px-3 py-2 text-tiny font-normal leading-tiny tracking-tightest text-primaryGray-3">
          Fax ID: {props.faxId}
        </div>
      )}
    </div>
  );
};

export default StatusBanner;
